import React, { createContext, useContext, useState, useCallback } from 'react';
import { NotificationToast, Notification, NotificationType } from './NotificationToast';
import { ConfirmToast, ConfirmOptions } from './ConfirmToast';
import './NotificationToast.css';
import './ConfirmToast.css';

interface NotificationContextValue {
    showNotification: (message: string, type?: NotificationType, duration?: number) => void;
    success: (message: string, duration?: number) => void;
    error: (message: string, duration?: number) => void;
    warning: (message: string, duration?: number) => void;
    info: (message: string, duration?: number) => void;
    confirm: (message: string, confirmText?: string, cancelText?: string) => Promise<boolean>;
    showLoading: (message: string) => string;
    hideLoading: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within NotificationProvider');
    }
    return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [confirms, setConfirms] = useState<ConfirmOptions[]>([]);

    const showNotification = useCallback((message: string, type: NotificationType = 'info', duration = 5000) => {
        const id = `notification-${Date.now()}-${Math.random()}`;
        const notification: Notification = { id, message, type, duration };

        setNotifications(prev => [...prev, notification]);
    }, []);

    const success = useCallback((message: string, duration = 5000) => {
        showNotification(message, 'success', duration);
    }, [showNotification]);

    const error = useCallback((message: string, duration = 5000) => {
        showNotification(message, 'error', duration);
    }, [showNotification]);

    const warning = useCallback((message: string, duration = 5000) => {
        showNotification(message, 'warning', duration);
    }, [showNotification]);

    const info = useCallback((message: string, duration = 5000) => {
        showNotification(message, 'info', duration);
    }, [showNotification]);

    const confirm = useCallback((message: string, confirmText?: string, cancelText?: string): Promise<boolean> => {
        return new Promise((resolve) => {
            const id = `confirm-${Date.now()}-${Math.random()}`;
            const confirmOptions: ConfirmOptions = {
                id,
                message,
                confirmText,
                cancelText,
                onConfirm: () => resolve(true),
                onCancel: () => resolve(false)
            };

            setConfirms(prev => [...prev, confirmOptions]);
        });
    }, []);

    const showLoading = useCallback((message: string): string => {
        const id = `loading-${Date.now()}-${Math.random()}`;
        const notification: Notification = { id, message, type: 'loading', duration: 0 };

        setNotifications(prev => [...prev, notification]);
        return id;
    }, []);

    const hideLoading = useCallback((id: string) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    }, []);

    const handleClose = useCallback((id: string) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    }, []);

    const handleConfirmClose = useCallback((id: string) => {
        setConfirms(prev => prev.filter(c => c.id !== id));
    }, []);

    return (
        <NotificationContext.Provider value={{ showNotification, success, error, warning, info, confirm, showLoading, hideLoading }}>
            {children}
            <div className="notification-toast-container">
                {confirms.map(confirmOptions => (
                    <ConfirmToast
                        key={confirmOptions.id}
                        options={confirmOptions}
                        onClose={handleConfirmClose}
                    />
                ))}
                {notifications.map(notification => (
                    <NotificationToast
                        key={notification.id}
                        notification={notification}
                        onClose={handleClose}
                    />
                ))}
            </div>
        </NotificationContext.Provider>
    );
};
